// ===========================================
// Dashboard — Tableau de bord refondu (UX + courtage assurance CI)
// 6 KPIs + 2 graphiques + listes opérationnelles + top/récents
// ===========================================
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Row, Col, Segmented, Button, Tooltip, Modal } from 'antd';
import {
    DollarOutlined,
    FileProtectOutlined,
    UserOutlined,
    ExclamationCircleOutlined,
    WarningOutlined,
    ClockCircleOutlined,
    ReloadOutlined,
    RiseOutlined,
    BankOutlined,
    QuestionCircleOutlined,
    CloseOutlined,
} from '@ant-design/icons';

import { AuthContext } from '../../common/constants';
import { dashboardService } from '../../common/services';
import { useGlobalAgence } from '../../common/hooks';
import { useT, useLocale } from '../../common/i18n';
import { formatDateLong } from '../../common/utils/format';

import KpiCard from './components/KpiCard';
import TrendChart from './components/TrendChart';
import DonutChart from './components/DonutChart';
import AlertsBanner from './components/AlertsBanner';
import EcheancesList from './components/EcheancesList';
import SinistresList from './components/SinistresList';
import { TopClients, RecentClients } from './components/ClientsCards';
import RecentContratsTable from './components/RecentContratsTable';

import './home.page.scss';

// ─── Sélecteur de période ─────────────────────────────────────────────────
const PERIOD_OPTIONS = [
    { label: '30 jours', value: '30j' },
    { label: '3 mois',   value: '3m'  },
    { label: '6 mois',   value: '6m'  },
    { label: '12 mois',  value: '12m' },
];

// Mapping période → mois de courbe CA (rolling)
const PERIOD_TO_MONTHS = { '30j': 6, '3m': 6, '6m': 6, '12m': 12 };

const getGreetingKey = () => {
    const h = new Date().getHours();
    if (h < 12) return 'dashboard.greeting.morning';
    if (h < 18) return 'dashboard.greeting.afternoon';
    return 'dashboard.greeting.evening';
};

const DashboardPage = () => {
    const t = useT();
    const { locale } = useLocale();
    const { currentUser } = useContext(AuthContext);

    const userName = (currentUser?.firstName || currentUser?.lastName || '').toUpperCase();

    // ─── État période globale ─────────────────────────────────────────
    const [periode, setPeriode] = useState('30j');
    const [guideOpen, setGuideOpen] = useState(false);
    const globalAgence = useGlobalAgence();

    // ─── État données ─────────────────────────────────────────────────
    const [loadingKpis,       setLoadingKpis]       = useState(true);
    const [kpisData,          setKpisData]          = useState(null);
    const [loadingEvol,       setLoadingEvol]       = useState(true);
    const [evolutionData,     setEvolutionData]     = useState([]);
    const [loadingRepartition,setLoadingRepartition]= useState(true);
    const [repartitionData,   setRepartitionData]   = useState([]);
    const [loadingEcheances,  setLoadingEcheances]  = useState(true);
    const [echeancesData,     setEcheancesData]     = useState(null);
    const [loadingSinistres,  setLoadingSinistres]  = useState(true);
    const [sinistresData,     setSinistresData]     = useState([]);
    const [loadingTop,        setLoadingTop]        = useState(true);
    const [topClientsData,    setTopClientsData]    = useState([]);
    const [loadingRecents,    setLoadingRecents]    = useState(true);
    const [recentClientsData, setRecentClientsData] = useState([]);
    const [loadingContrats,   setLoadingContrats]   = useState(true);
    const [recentContratsData, setRecentContratsData] = useState([]);
    const [alertesData,       setAlertesData]       = useState([]);

    // ─── Fetch global ─────────────────────────────────────────────────
    const fetchAll = useCallback(async () => {
        const agence = globalAgence !== 'all' ? globalAgence : undefined;
        const params = { periode, agence };

        // KPIs
        setLoadingKpis(true);
        dashboardService.getKpis(params)
            .then((res) => setKpisData(res?.data?.kpis || res?.kpis || null))
            .catch(() => setKpisData(null))
            .finally(() => setLoadingKpis(false));

        // Évolution CA (toujours en rolling months pour le graphique)
        setLoadingEvol(true);
        dashboardService.getCaEvolution({ months: PERIOD_TO_MONTHS[periode] || 6, agence })
            .then((res) => setEvolutionData(res?.data?.evolution || res?.evolution || []))
            .catch(() => setEvolutionData([]))
            .finally(() => setLoadingEvol(false));

        // Répartition par type d'assurance
        setLoadingRepartition(true);
        dashboardService.getRepartitionTypes({ agence })
            .then((res) => setRepartitionData(res?.data || []))
            .catch(() => setRepartitionData([]))
            .finally(() => setLoadingRepartition(false));

        // Échéances 7/15/30 jours
        setLoadingEcheances(true);
        dashboardService.getEcheances({ agence })
            .then((res) => setEcheancesData(res?.data || null))
            .catch(() => setEcheancesData(null))
            .finally(() => setLoadingEcheances(false));

        // Sinistres en cours
        setLoadingSinistres(true);
        dashboardService.getSinistresEnCours({ limit: 8, agence })
            .then((res) => setSinistresData(res?.data || []))
            .catch(() => setSinistresData([]))
            .finally(() => setLoadingSinistres(false));

        // Top clients
        setLoadingTop(true);
        dashboardService.getTopClients({ periode, limit: 5, agence })
            .then((res) => setTopClientsData(res?.data || []))
            .catch(() => setTopClientsData([]))
            .finally(() => setLoadingTop(false));

        // Derniers clients
        setLoadingRecents(true);
        dashboardService.getDerniersClients({ limit: 5, agence })
            .then((res) => setRecentClientsData(res?.data || []))
            .catch(() => setRecentClientsData([]))
            .finally(() => setLoadingRecents(false));

        // Derniers contrats
        setLoadingContrats(true);
        dashboardService.getDerniersContrats({ agence })
            .then((res) => setRecentContratsData(res?.data || []))
            .catch(() => setRecentContratsData([]))
            .finally(() => setLoadingContrats(false));

        // Alertes (silencieux, pas de loading visible)
        dashboardService.getAlertes({ agence })
            .then((res) => setAlertesData(res?.data?.alertes || res?.alertes || []))
            .catch(() => setAlertesData([]));
    }, [periode, globalAgence]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // ─── Sélection période : libellé du label de période courante ────
    const periodLabel = useMemo(() => {
        const opt = PERIOD_OPTIONS.find((o) => o.value === periode);
        return opt ? opt.label : '30 jours';
    }, [periode]);

    const todayStr = useMemo(() => formatDateLong(new Date()), [locale]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Construction des KPIs depuis les données API ────────────────
    const kpis = kpisData || {};

    return (
        <div className="dashboard-page">
            {/* Header : greeting + date + sélecteur période + bouton refresh */}
            <header className="dashboard-header">
                <div className="dashboard-header-left">
                    <h1 className="dashboard-greeting">
                        {t(getGreetingKey())} {userName} <span aria-hidden>👋</span>
                    </h1>
                    <div className="dashboard-date">{todayStr}</div>
                </div>
                <div className="dashboard-header-right">
                    <Button
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setGuideOpen(true)}
                        style={{ borderRadius: 8, background: '#1A2B5F', borderColor: '#1A2B5F', color: '#fff', fontWeight: 600 }}
                    >
                        Guide
                    </Button>
                    <Segmented
                        value={periode}
                        onChange={setPeriode}
                        options={PERIOD_OPTIONS}
                        size="middle"
                    />
                    <Tooltip title={t('dashboard.refresh')}>
                        <Button
                            type="text"
                            icon={<ReloadOutlined />}
                            onClick={fetchAll}
                            aria-label={t('dashboard.refresh')}
                        />
                    </Tooltip>
                </div>
            </header>

            <Modal
                open={guideOpen}
                onCancel={() => setGuideOpen(false)}
                title={<span><QuestionCircleOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />Guide — Tableau de bord</span>}
                closeIcon={(
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                        background: '#C8102E', border: '1px solid #C8102E', borderRadius: 8,
                        fontSize: '0.9rem', fontWeight: 600, color: '#fff',
                    }}>
                        <CloseOutlined /> Fermer
                    </span>
                )}
                footer={<Button type="primary" onClick={() => setGuideOpen(false)}>J'ai compris</Button>}
                width={640}
            >
                <span style={{ color: 'var(--cap-text-muted, #8c8c8c)' }}>
                    Le tableau de bord donne une vue synthétique de l'activité. Pour une aide
                    complète sur toute l'application, ouvrez le <strong>Centre d'aide</strong>
                    (icône <QuestionCircleOutlined /> en haut à droite).
                </span>
                {(() => {
                    const liStyle = {
                        display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 10,
                        background: 'var(--cap-surface-2, #f6f8fb)', border: '1px solid var(--cap-border, #E8ECF1)',
                        borderRadius: 12, lineHeight: 1.5,
                    };
                    const icStyle = { flexShrink: 0, fontSize: 18, color: '#1A2B5F', marginTop: 2 };
                    return (
                        <div style={{ marginTop: 16 }}>
                            <div style={liStyle}>
                                <ClockCircleOutlined style={icStyle} />
                                <div>
                                    <strong>1 · Période</strong> — Le sélecteur
                                    <strong> 30 j / 3 / 6 / 12 mois</strong> ajuste tous les
                                    indicateurs et leurs évolutions (ex. « +83 % vs période
                                    précédente »).
                                </div>
                            </div>
                            <div style={liStyle}>
                                <ExclamationCircleOutlined style={icStyle} />
                                <div>
                                    <strong>2 · Actions urgentes</strong> — Le bandeau rouge liste
                                    ce qui demande une intervention (impayés anciens, échéances…).
                                    Le bouton « Voir » ouvre la liste concernée.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <DollarOutlined style={icStyle} />
                                <div>
                                    <strong>3 · Indicateurs clés</strong> — Chiffre d'affaires,
                                    production, encaissé, à recouvrer, sinistres en cours,
                                    échéances &lt; 30 j, clients actifs et ticket moyen, recalculés
                                    selon la période et l'agence sélectionnée en haut.
                                </div>
                            </div>
                            <div style={{ ...liStyle, marginBottom: 0 }}>
                                <FileProtectOutlined style={icStyle} />
                                <div>
                                    <strong>4 · Derniers contrats créés</strong> — Aperçu rapide
                                    avec recherche ; « Voir tous » ouvre la liste complète des
                                    contrats, « Détail » ouvre le contrat.
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            {/* Bandeau alertes critiques (conditionnel) */}
            <AlertsBanner alertes={alertesData} />

            {/* Grid 8 KPIs (4 par ligne sur desktop large) */}
            <section className="dashboard-kpis-grid" aria-label="Indicateurs clés">
                <KpiCard
                    label="Chiffre d'affaires"
                    value={kpis.ca?.value}
                    isCurrency
                    icon={<DollarOutlined />}
                    tone="green"
                    delta={kpis.ca?.delta}
                    subtitle={`vs ${periodLabel} précédent`}
                    loading={loadingKpis}
                    to="/dashboard/reglements"
                />
                <KpiCard
                    label="Production"
                    value={kpis.production?.value}
                    icon={<FileProtectOutlined />}
                    tone="blue"
                    delta={kpis.production?.delta}
                    subtitle={`nouveau${(kpis.production?.value || 0) > 1 ? 'x' : ''} contrat${(kpis.production?.value || 0) > 1 ? 's' : ''}`}
                    loading={loadingKpis}
                    to="/dashboard/contrats"
                />
                <KpiCard
                    label="Encaissé"
                    value={kpis.encaisse?.value}
                    isCurrency
                    icon={<BankOutlined />}
                    tone="purple"
                    subtitle={`${kpis.encaisse?.count || 0} versement${(kpis.encaisse?.count || 0) > 1 ? 's' : ''}`}
                    loading={loadingKpis}
                    to="/dashboard/reglements"
                />
                <KpiCard
                    label="À recouvrer"
                    value={kpis.impayes?.value}
                    isCurrency
                    icon={<ExclamationCircleOutlined />}
                    tone="red"
                    subtitle={`${kpis.impayes?.count || 0} impayé${(kpis.impayes?.count || 0) > 1 ? 's' : ''}`}
                    badge={kpis.impayes?.count}
                    loading={loadingKpis}
                    to="/dashboard/impayes"
                />
                <KpiCard
                    label="Sinistres en cours"
                    value={kpis.sinistres?.value}
                    icon={<WarningOutlined />}
                    tone="orange"
                    subtitle="à instruire"
                    loading={loadingKpis}
                    to="/dashboard/sinistres"
                />
                <KpiCard
                    label="Échéances < 30j"
                    value={kpis.echeances?.value}
                    icon={<ClockCircleOutlined />}
                    tone="grey"
                    subtitle={
                        kpis.echeances?.urgent > 0
                            ? `${kpis.echeances.urgent} urgent${kpis.echeances.urgent > 1 ? 'es' : 'e'} (< 7j)`
                            : 'à renouveler'
                    }
                    loading={loadingKpis}
                    to="/dashboard/echeances"
                />
                <KpiCard
                    label="Clients actifs"
                    value={kpis.clients?.value}
                    icon={<UserOutlined />}
                    tone="indigo"
                    delta={kpis.clients?.delta}
                    subtitle={`+${kpis.clients?.nouveaux || 0} sur la période`}
                    loading={loadingKpis}
                    to="/dashboard/clients"
                />
                <KpiCard
                    label="Ticket moyen"
                    value={
                        kpis.encaisse?.count > 0
                            ? Math.round((kpis.encaisse?.value || 0) / kpis.encaisse.count)
                            : 0
                    }
                    isCurrency
                    icon={<RiseOutlined />}
                    tone="teal"
                    subtitle={`par versement (${kpis.encaisse?.count || 0})`}
                    loading={loadingKpis}
                    to="/dashboard/reglements"
                />
            </section>

            {/* Section tableau des derniers contrats créés (juste après les KPIs) */}
            <section className="dashboard-contrats" aria-label="Derniers contrats">
                <RecentContratsTable
                    data={recentContratsData}
                    loading={loadingContrats}
                />
            </section>

            {/* Section graphiques : courbe CA + donut répartition */}
            <section className="dashboard-charts" aria-label="Performance">
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={14}>
                        <TrendChart
                            data={evolutionData}
                            loading={loadingEvol}
                            title={`Évolution du CA — ${PERIOD_TO_MONTHS[periode] || 6} derniers mois`}
                        />
                    </Col>
                    <Col xs={24} lg={10}>
                        <DonutChart
                            data={repartitionData}
                            loading={loadingRepartition}
                            title="Répartition par type d'assurance"
                            subtitle="Contrats actifs"
                        />
                    </Col>
                </Row>
            </section>

            {/* Section activité opérationnelle : échéances + sinistres */}
            <section className="dashboard-operations" aria-label="Activité opérationnelle">
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={14}>
                        <EcheancesList data={echeancesData} loading={loadingEcheances} />
                    </Col>
                    <Col xs={24} lg={10}>
                        <SinistresList data={sinistresData} loading={loadingSinistres} />
                    </Col>
                </Row>
            </section>

            {/* Section top + récents (en bas de page : portefeuille clients) */}
            <section className="dashboard-clients" aria-label="Portefeuille clients">
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                        <TopClients data={topClientsData} loading={loadingTop} />
                    </Col>
                    <Col xs={24} lg={12}>
                        <RecentClients data={recentClientsData} loading={loadingRecents} />
                    </Col>
                </Row>
            </section>
        </div>
    );
};

export default DashboardPage;
